import React, { useRef, useState } from 'react';
import { FileText, UploadCloud, X } from 'lucide-react';

const ACCEPTED_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png'];

const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const PolicyDocumentDropzone = ({ file, onFileSelect }) => {
    const inputRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [error, setError] = useState('');

    const selectFile = (nextFile) => {
        if (!nextFile) return;

        const extension = nextFile.name.split('.').pop()?.toLowerCase();
        if (!ACCEPTED_EXTENSIONS.includes(extension)) {
            setError('Please select a PDF, JPG, JPEG, or PNG file.');
            if (inputRef.current) inputRef.current.value = '';
            return;
        }

        setError('');
        onFileSelect(nextFile);
    };

    const openFilePicker = () => inputRef.current?.click();

    const handleDrop = (event) => {
        event.preventDefault();
        setIsDragging(false);
        selectFile(event.dataTransfer.files?.[0]);
    };

    const removeFile = (event) => {
        event.stopPropagation();
        setError('');
        onFileSelect(null);
        if (inputRef.current) inputRef.current.value = '';
    };

    return (
        <div className="policy-document-field">
            <label>Policy Document</label>
            <div
                className={`policy-document-dropzone${isDragging ? ' is-dragging' : ''}${file ? ' has-file' : ''}`}
                role="button"
                tabIndex={0}
                onClick={openFilePicker}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        openFilePicker();
                    }
                }}
                onDragEnter={(event) => {
                    event.preventDefault();
                    setIsDragging(true);
                }}
                onDragOver={(event) => {
                    event.preventDefault();
                    event.dataTransfer.dropEffect = 'copy';
                    setIsDragging(true);
                }}
                onDragLeave={(event) => {
                    if (!event.currentTarget.contains(event.relatedTarget)) setIsDragging(false);
                }}
                onDrop={handleDrop}
            >
                <input
                    ref={inputRef}
                    className="policy-document-file-input"
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(event) => selectFile(event.target.files?.[0])}
                    tabIndex={-1}
                />
                {file ? (
                    <div className="policy-document-selected-file">
                        <FileText size={30} aria-hidden="true" />
                        <div>
                            <strong>{file.name}</strong>
                            <span>{formatFileSize(file.size)}</span>
                        </div>
                        <button type="button" onClick={removeFile} aria-label="Remove selected file">
                            <X size={18} aria-hidden="true" />
                        </button>
                    </div>
                ) : (
                    <>
                        <UploadCloud size={38} aria-hidden="true" />
                        <strong>{isDragging ? 'Drop the document here' : 'Drag and drop the document here'}</strong>
                        <span>or click to browse</span>
                        <small>PDF, JPG, JPEG, or PNG</small>
                    </>
                )}
            </div>
            {error && <p className="policy-document-file-error" role="alert">{error}</p>}
        </div>
    );
};

export default PolicyDocumentDropzone;
